import { AfterContentInit, Component, DestroyRef, DOCUMENT, inject, NgZone, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, Router, RouterModule } from '@angular/router';
import { LoaderComponent } from '@h5s/design-system';
import { FeatureSystemService } from '@h5s/feature-system';
import { AuthService, UtilAuth } from '@h5s/util-auth';
import { filter, share, take } from 'rxjs';

const TRANSITION_DURATION = 700;

@Component({
  imports: [RouterModule, LoaderComponent],
  selector: 'h5s-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterContentInit {
  /** Services */
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly featureSystemService = inject(FeatureSystemService);
  private readonly authService = inject(AuthService);

  /** Titolo dell'applicazione */
  public title = `ZStay - Zucchetti`;

  /** Observable che emette gli eventi NavigationCanncel e NavigationEnd del router */
  private readonly navigationEndOrCancel$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationCancel || event instanceof NavigationEnd),
    share(),
  );

  /** @inheritdoc */
  public ngOnInit(): void {
    /**
     * Pulisce l'URL dall'hash dopo il login
     * L'operazione viene eseguita una sola volta
     * Se l'utente si disconnette e si riconnette, l'operazione viene eseguita nuovamente
     */
    this.navigationEndOrCancel$.pipe(take(1)).subscribe(() => {
      UtilAuth.cleanHashFromUrlAfterLogin();
      this.restorePendingRedirect();
    });

    /** Carica allo startup la versione del backend */
    this.featureSystemService.getBackendVersion();
  }

  /**
   * Ripristina la rotta che l'utente stava cercando di raggiungere prima del login.
   *
   * Il redirect URI registrato sull'Identity Provider è fisso per bundle (exact match
   * richiesto da OAuth 2.0), quindi il deep link viaggia nel parametro `state` e va
   * ripristinato qui: una navigazione vera, non un replaceState, perché il router ha già
   * letto l'URL del browser prima che tryLogin risolvesse.
   */
  private restorePendingRedirect(): void {
    if (this.router.url.startsWith('/pick-your-place')) return;

    const pendingUrl = this.authService.consumePendingRedirect();

    // navigateByUrl e non NavigationService.navigate: quest'ultimo scarta il fragment e
    // ri-encoda i query value tramite HttpParams, mentre l'app trasporta blob base64
    // nella query che devono sopravvivere byte per byte.
    if (pendingUrl) this.router.navigateByUrl(pendingUrl, { replaceUrl: true });
  }

  /** @inheritdoc */
  public ngAfterContentInit(): void {
    /** Al primo caricamento completo nasconde il loader */
    this.navigationEndOrCancel$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.hideLoader());
  }

  /**
   * Nasconde il loader
   */
  private hideLoader(): void {
    this.ngZone.runOutsideAngular(() => {
      // Nascondiamo il loader
      const loaderElement = this.document.querySelector('.z-loader');
      loaderElement?.classList.add('loading-complete');

      setTimeout(() => {
        loaderElement?.remove();
      }, TRANSITION_DURATION);
    });
  }
}
